import React, { useEffect, useState } from 'react'
import { Box, Button, Dialog, DialogActions, DialogContent } from '@mui/material'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { GetUtcTime } from '../../generic/DateTimeFunctions'
import { BRANCH, computeJobTimeMs } from './processTree'
import ProcessTreeJobForm from './ProcessTreeJobForm'
import JobTaskTable from './JobTaskTable'
import JobResources from './JobResources'
import { DASH, Hint, LinkChip, P, SectionTitle, SkippedMark, T, TAB, TABULAR, TabStrip, Tile, Trail } from './ProcessTreeInspectorParts'
import { NIGHT_JOB_CONTEXT_OFF } from './jobNightCharacter'

/**
 * ProcessTreeJobDialog.jsx
 * The operation editor the process Tree opens when a job row is clicked a second time (PO review of
 * 2026-09-11): the SAME information the inspector on the right shows — general properties, tasks and
 * material — in a window of its own.
 *
 * It has NO DRAFT (story #150, RN140-RN143). It used to edit a copy and commit it on Save, and that
 * copy was the defect: it captured the tasks as they were when the window opened, so editing the tasks
 * and then a general property made the second write undo the first. Every change is now forwarded to
 * the model as it happens, through the writers the caller hands in — the graph's own reducer actions —
 * and everything on screen is read from the live node. The footer says so, and Close only closes.
 *
 * What is written here is still an UNSAVED change of the editor: the workflow is persisted by the
 * editor's Save, exactly as a change made in the graph's job panel is.
 *
 * The tasks are the graph's own table (D16), the same component the inspector mounts. The material is
 * read-only here as it is in the inspector: the BOM is assigned from Resources.
 *
 * Read-only when the caller says so (`editable` false): the fields are divs and the hint explains why —
 * the same rule as the inspector (S83).
 */

/**
 * PO review 2026-09-22: la ventana se adapta a lo que ensena. Tenia UN tamano fijo para las tres
 * pestanas, y en Material ahi dentro va el panel de recursos entero — tabla mas paleta arrastrable, dos
 * paneles uno al lado del otro — en 960 px: estrecho e incomodo, que es justo lo que se reporto.
 *
 * General y Tareas son formularios en columna y no ganan nada por ser anchos; estirarlas a lo que pide
 * la tabla solo dejaria campos flotando en medio de un descampado. Por eso el ancho lo dicta la pestana
 * y no un numero unico.
 *
 * Las unidades relativas son el "adaptarse a todo": en un portatil manda el viewport, en un monitor
 * grande manda el tope, y nunca se sale de la pantalla.
 */
const paperSxFor = (tab) => ({
  background: P.background.color_373636 + ' !important',
  width: tab === TAB.MATERIAL ? 'min(1480px, 97vw)' : 'min(960px, 96vw)',
  maxWidth: 'none',
  height: tab === TAB.MATERIAL ? 'min(92vh, 1040px)' : 'min(82vh, 900px)',
  display: 'flex',
  flexDirection: 'column',
  // El cambio de tamano al saltar de pestana se acompana; de golpe da un respingo.
  transition: 'width 200ms ease, height 200ms ease',
  '@media (prefers-reduced-motion: reduce)': { transition: 'none' }
})

/**
 * The body writes STRAIGHT to the model and reads straight from it (story #150, RN140-RN143).
 *
 * It used to hold a draft and commit it on Save, which is what made it possible to edit the tasks, then
 * a general property, and have the second write undo the first — the draft had captured the tasks as
 * they were when the dialog opened. There is no draft now: every change is forwarded as it happens, and
 * everything on screen is read from the `node` prop, so what is shown is always the live model.
 */
const JobDialogBody = ({
  node, trail, depth, branchIndex, branchCount, coverage,
  showCounters, showTasks, showMaterial, editable, editableTasks, editHint, requiredNodes,
  onClose, onUpdateJobReference, onUpdateTasks, onUpdateNodeName, onUpdateNodeCode, onUpdateTechnology, onSelectNode,
  progressCalculationTypes, indexValues, workflowUseIndex, showMachineTime, featureGroupRevisionControlled, featureGroupBaselines, cascadeApprovalEnabled, fabricationOrderOperable, nightJobContext,
  workflowName, partReference, getTempUUID, setTempUUID,
  editableMaterial, nested, resourceTypes, showAlert, parentPartReference, partReferenceId, workflow,
  onEditingRowChanged,
  // La pestana la gobierna la ventana, que es quien se dimensiona segun cual este abierta.
  tab, onTabChange
}) => {
  const { t_ } = useSmartTranslation()

  const jobReference = node.jobReference || {}
  const tasks = jobReference.taskReferences || []

  const jobTime = computeJobTimeMs(jobReference)
  const durationValue = jobTime.isUnknown ? DASH : GetUtcTime(jobTime.ms)
  const operators = jobReference.operatorQuantity ?? 0
  const minimumQuantity = jobReference.minimumQuantity || 1

  const tabs = [
    { value: TAB.GENERAL, label: t_('General') },
    ...(showTasks ? [{ value: TAB.TASKS, label: t_('Tasks') }] : []),
    ...(showMaterial ? [{ value: TAB.MATERIAL, label: t_('Material') }] : [])
  ]

  // One interaction, one action. The technology dialog already carries the derived field with it, so
  // nothing else has to be written behind it (RN141.3).
  const changeTechnology = (technology) => {
    onUpdateTechnology(node.id, technology)
  }

  return (
    <>
      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', minHeight: 0, fontFamily: T.sans, color: T.textMain }}>
        <Box sx={{ padding: '16px 20px 0', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <Trail steps={trail} />
          <Box data-testid='job-dialog-type' sx={{ fontSize: '11px', fontWeight: 800, letterSpacing: '.16em', textTransform: 'uppercase', color: T.accent }}>
            {editable ? t_('Edit operation') : t_('Operation')}
          </Box>
          <Box data-testid='job-dialog-identity' sx={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0, flexWrap: 'wrap' }}>
            <Box component='span' sx={{ flexGrow: 1, minWidth: 0, fontSize: '20px', fontWeight: 700, lineHeight: 1.2, overflowWrap: 'anywhere' }}>
              {node.name || node.code || ''}
            </Box>
            {node.name && node.code
              ? <Box component='span' sx={{ flex: '0 0 auto', fontSize: '12.5px', ...TABULAR, color: T.textFaint, whiteSpace: 'nowrap' }}>{node.code}</Box>
              : null}
            {node.skipped ? <SkippedMark label={t_('Skipped')} /> : null}
          </Box>
        </Box>
        <TabStrip idPrefix='job-dialog' tabs={tabs} active={tabs.some((entry) => entry.value === tab) ? tab : TAB.GENERAL} onChange={onTabChange} />
        <Box sx={{ flexGrow: 1, overflowY: 'auto', overflowX: 'hidden', padding: '16px 20px', minHeight: 0 }}>
          {!editable && editHint !== '' ? <Hint testId='job-dialog-edit-hint' text={editHint} /> : null}

          {tab === TAB.GENERAL || !tabs.some((entry) => entry.value === tab)
            ? (
              <Box data-testid='job-dialog-general' sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {showCounters
                  ? (
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: '8px' }}>
                      <Tile testId='job-dialog-counter-tasks' label={t_('Tasks')} value={String(tasks.length)} />
                      <Tile testId='job-dialog-counter-duration' label={t_('Duration')} value={durationValue} accent />
                      <Tile testId='job-dialog-counter-operators' label={t_('Operators')} value={String(operators)} />
                      <Tile testId='job-dialog-counter-quantity' label={t_('Minimum quantity')} value={String(minimumQuantity)} />
                    </Box>
                    )
                  : null}
                {requiredNodes.length > 0
                  ? (
                    <Box>
                      <SectionTitle label={t_('Depends on')} />
                      <Box data-testid='job-dialog-required' sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        {requiredNodes.map((required) => (
                          <LinkChip key={required.nodeId} testId={'job-dialog-required-' + required.nodeId} label={required.label} branch={BRANCH.OK} onClick={() => { onClose(); onSelectNode(required.nodeId) }} />
                        ))}
                      </Box>
                    </Box>
                    )
                  : null}
                <ProcessTreeJobForm
                  idPrefix='job-dialog'
                  node={node}
                  jobReference={jobReference}
                  depth={depth}
                  branchIndex={branchIndex}
                  branchCount={branchCount}
                  editable={editable}
                  showCounters={showCounters}
                  durationValue={durationValue}
                  onChange={onUpdateJobReference}
                  onChangeNodeName={(name) => onUpdateNodeName(node.id, name)}
                  onChangeNodeCode={(code) => onUpdateNodeCode(node.id, code)}
                  onChangeTechnology={changeTechnology}
                  progressCalculationTypes={progressCalculationTypes}
                  indexValues={indexValues}
                  workflowUseIndex={workflowUseIndex}
                  cascadeApprovalEnabled={cascadeApprovalEnabled}
                  featureGroupRevisionControlled={featureGroupRevisionControlled}
                  fabricationOrderOperable={fabricationOrderOperable}
                  nightJobContext={nightJobContext}
                />
              </Box>
              )
            : null}

          {tab === TAB.TASKS && showTasks
            ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <SectionTitle label={t_('Tasks')} />
                {/* Story #150 (D16): the graph's own table, the same one the side panel mounts. */}
                <JobTaskTable
                  node={node}
                  editing={editableTasks}
                  updateTaskList={onUpdateTasks}
                  onEditingRowChanged={(isOpen) => onEditingRowChanged('tree-dialog-tasks', isOpen)}
                  workflowName={workflowName}
                  partReference={partReference}
                  getTempUUID={getTempUUID}
                  setTempUUID={setTempUUID}
                  indexValues={indexValues}
                  workflowUseIndex={workflowUseIndex}
                  showMachineTime={showMachineTime}
                  featureGroupRevisionControlled={featureGroupRevisionControlled}
                  featureGroupBaselines={featureGroupBaselines}
                />
              </Box>
              )
            : null}

          {showMaterial
            ? (
              /*
               * Mounted once and hidden with `display`, never unmounted: the resources panel fetches the
               * BOM palette when it mounts, so tearing it down on every tab change would re-fetch it
               * each time the user looks away and back. Same pattern the workflow surface switch uses.
               */
              <Box sx={{ display: tab === TAB.MATERIAL ? 'flex' : 'none', flexDirection: 'column', gap: '16px' }}>
                <SectionTitle label={t_('Resources')} />
                <JobResources
                  editing={editableMaterial}
                  resources={node.jobReference?.resources || []}
                  partReferenceId={partReferenceId}
                  onResourcesChanged={(newResources) => onUpdateJobReference({ ...node.jobReference, resources: newResources })}
                  resourceTypes={resourceTypes}
                  showAlert={showAlert}
                  parentPartReference={parentPartReference}
                  workflow={workflow}
                  // A nested job is shown whole and read-only: its material belongs to its own process
                  // (D9), so the draggable palette has nothing to do here.
                  showPalette={!nested}
                  // RN117.6: a resource row left open in this window blocks the save too (D14).
                  onEditingRowChanged={(isOpen) => onEditingRowChanged('tree-dialog-resources', isOpen)}
                />
              </Box>
              )
            : null}
        </Box>
      </DialogContent>
      <DialogActions sx={{ borderTop: '1px solid ' + T.borderSoft, padding: '10px 20px' }}>
        {/* RN142/POL105: with nothing to Save here, the footer says where what was written does get
            saved, so Close never looks like it might be throwing the changes away. */}
        <Box data-testid='job-dialog-footer-note' sx={{ mr: 'auto', fontSize: '12px', color: T.textFaint }}>
          {t_('Changes are saved with the process')}
        </Box>
        <Button variant='outlined' data-testid='job-dialog-close' onClick={onClose} sx={{ textTransform: 'none' }}>
          {t_('Close')}
        </Button>
      </DialogActions>
    </>
  )
}

const ProcessTreeJobDialog = ({
  open = false,
  node = null,
  trail = [],
  depth = 0,
  branchIndex = 0,
  branchCount = 0,
  coverage = null,
  showCounters = true,
  showTasks = true,
  showMaterial = true,
  editable = false,
  editableTasks = false,
  editHint = '',
  requiredNodes = [],
  onClose = () => {},
  onUpdateJobReference = () => {},
  onUpdateTasks = () => {},
  onUpdateNodeName = () => {},
  onUpdateNodeCode = () => {},
  onUpdateTechnology = () => {},
  onSelectNode = () => {},
  progressCalculationTypes = [],
  indexValues = [],
  workflowUseIndex = false,
  // Story 208 (S31): SHOW_TASK_MACHINE_TIME, handed to the task table of the Tasks tab.
  showMachineTime = false,
  featureGroupRevisionControlled = false,
  featureGroupBaselines = {},
  cascadeApprovalEnabled = false,
  fabricationOrderOperable = true,
  nightJobContext = NIGHT_JOB_CONTEXT_OFF,
  // Story #150: the window hosts the full resources panel now, so it needs what that panel needs.
  initialTab = null,
  editableMaterial = false,
  nested = false,
  resourceTypes = [],
  showAlert = () => {},
  parentPartReference = null,
  partReferenceId = null,
  workflow = {},
  onEditingRowChanged = () => {},
  // What the extracted task table needs, threaded from ProcessTreeView.
  workflowName = '',
  partReference = null,
  getTempUUID = () => undefined,
  setTempUUID = () => {}
}) => {
  const [tab, setTab] = useState(initialTab || TAB.GENERAL)
  // Cambiar de operacion, o pedir explicitamente una pestana, vuelve a empezar por ella. Antes lo hacia
  // el `key={node.id}` del cuerpo; al subir el estado hay que decirlo aqui.
  useEffect(() => {
    setTab(initialTab || TAB.GENERAL)
  }, [node?.id, initialTab])

  return (
    <Dialog
      open={open && !!node}
      onClose={onClose}
      data-testid='process-tree-job-dialog'
      data-tab={tab}
      // Si el material se puede editar, visible en el DOM: es la puerta que ya se ha cerrado sola dos
      // veces por mirar la fila marcada en vez de la operacion, y desde fuera no se ve de otra forma.
      data-editable-material={editableMaterial ? 'true' : 'false'}
      PaperProps={{ sx: paperSxFor(tab) }}
    >
      {node
        ? (
          <JobDialogBody
            key={node.id}
            node={node}
            trail={trail}
            depth={depth}
            branchIndex={branchIndex}
            branchCount={branchCount}
            coverage={coverage}
            showCounters={showCounters}
            showTasks={showTasks}
            showMaterial={showMaterial}
            editable={editable}
            editableTasks={editableTasks}
            editHint={editHint}
            requiredNodes={requiredNodes}
            onClose={onClose}
            onUpdateJobReference={onUpdateJobReference}
            onUpdateTasks={onUpdateTasks}
            onUpdateNodeName={onUpdateNodeName}
            onUpdateNodeCode={onUpdateNodeCode}
            onUpdateTechnology={onUpdateTechnology}
            onSelectNode={onSelectNode}
            progressCalculationTypes={progressCalculationTypes}
            indexValues={indexValues}
            workflowUseIndex={workflowUseIndex}
            showMachineTime={showMachineTime}
            featureGroupRevisionControlled={featureGroupRevisionControlled}
            featureGroupBaselines={featureGroupBaselines}
            cascadeApprovalEnabled={cascadeApprovalEnabled}
            fabricationOrderOperable={fabricationOrderOperable}
            nightJobContext={nightJobContext}
            workflowName={workflowName}
            partReference={partReference}
            getTempUUID={getTempUUID}
            setTempUUID={setTempUUID}
            tab={tab}
            onTabChange={setTab}
            editableMaterial={editableMaterial}
            nested={nested}
            resourceTypes={resourceTypes}
            showAlert={showAlert}
            parentPartReference={parentPartReference}
            partReferenceId={partReferenceId}
            workflow={workflow}
            onEditingRowChanged={onEditingRowChanged}
          />
          )
        : null}
    </Dialog>
  )
}

export default ProcessTreeJobDialog

export { paperSxFor }
